class PivotInvoiceForm extends HTMLElement {
  get $form() {
    return this.querySelector('form');
  }

  get $label() {
    return this.querySelector('.cart-discount__label');
  }

  get $button() {
    return this.querySelector('button[type="submit"]');
  }

  get $clearButton() {
    return this.querySelector('[data-pivot-invoice-clear]');
  }

  get $rutInput() {
    return this.querySelector('input[name="attributes[Ruc]"]');
  }

  get $rutErrorMessage() {
    return this.querySelector('.rut-error-message');
  }

  get $summary() {
    return this.querySelector('[data-invoice-summary]');
  }

  get $summaryText() {
    return this.querySelector('[data-invoice-summary-text]');
  }

  get $panel() {
    return this.querySelector('[data-invoice-panel]');
  }

  get $tipoRadios() {
    return Array.from(this.querySelectorAll('input[name="attributes[TipoCliente]"]'));
  }

  get $groups() {
    return {
      persona: this.querySelector('.pivot-invoice-group[data-mode="persona"]'),
      empresa: this.querySelector('.pivot-invoice-group[data-mode="empresa"]'),
    };
  }

  connectedCallback() {
    if (this.__init) return;
    this.__init = true;

    this.addEventListener('submit', this.handleSubmit);
    this.addEventListener('click', this.handleClick);
    this.addEventListener('input', this.handleInput);
    this.addEventListener('change', this.handleChange);

    this.handleModeChange();
    this.checkFormState();
    this.renderSelectorState();

    this.checkoutObserver = new MutationObserver(() => this.updateCheckout());
    this.checkoutObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  disconnectedCallback() {
    this.checkoutObserver?.disconnect();
    this.__init = false;
    this.removeEventListener('submit', this.handleSubmit);
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('input', this.handleInput);
    this.removeEventListener('change', this.handleChange);
  }

  handleClick = (event) => {
    if (event.target.closest('[data-pivot-invoice-clear]')) {
      this.handleClear();
    }
    if (event.target.closest('[data-pivot-invoice-edit]')) {
      this.editing = true;
      this.renderSelectorState();
    }
  };

  handleInput = (event) => {
    if (event.target === this.$rutInput) {
      this.handleRucChange();
      return;
    }
    if (event.target.matches('input')) {
      this.checkFormState();
    }
  };

  handleChange = (event) => {
    if (event.target.name === 'attributes[TipoCliente]') {
      this.handleModeChange();
    }
    if (event.target.name === 'pivot-invoice-doctype') {
      this.handleDoctypeChange(event.target.value);
    }
  };

  getMode = () => {
    const checked = this.$tipoRadios.find((radio) => radio.checked);
    return checked ? checked.value : 'persona';
  };

  handleModeChange = () => {
    const mode = this.getMode();

    Object.entries(this.$groups).forEach(([groupMode, group]) => {
      const isActive = groupMode === mode;
      group.hidden = !isActive;
      group.querySelectorAll('input').forEach((input) => {
        input.disabled = !isActive;
        input.required = isActive;
        if (!isActive) input.setCustomValidity('');
      });
    });

    this.checkFormState();
  };

  handleDoctypeChange = (doctype) => {
    const isFactura = doctype === 'factura';

    if (!isFactura && this.dataset.saved === 'true') {
      this.handleClear();
    }

    this.editing = false;
    this.renderSelectorState();
  };

  renderSelectorState = () => {
    if (!this.$panel) return;

    const isFactura = this.isFacturaSelected();
    const isSaved = this.dataset.saved === 'true';
    const showSummary = isFactura && isSaved && !this.editing;

    this.$summary.hidden = !showSummary;
    this.$panel.hidden = !isFactura || showSummary;
    this.updateCheckout();
  };

  buildSummaryText = (data) => {
    const ruc = data['attributes[Ruc]'];
    const name =
      data['attributes[TipoCliente]'] === 'empresa'
        ? data['attributes[RazonSocial]']
        : `${data['attributes[Nombre]']} ${data['attributes[Apellido]'] || ''}`.trim();
    return `${name} - ${ruc}`;
  };

  setCheckoutDisabled = (disabled) => {
    const checkout = this.closest('cart-items-component')?.querySelector('button[name="checkout"]')
      ?? document.getElementById('checkout');
    if (checkout) checkout.disabled = disabled;
  };

  isFacturaSelected = () =>
    this.querySelector('input[name="pivot-invoice-doctype"]:checked')?.value === 'factura';

  isFormDirty = () =>
    Array.from(this.$form.querySelectorAll('input:not(:disabled)')).some(
      (input) => input.value !== input.defaultValue
    );

  shouldBlockCheckout = () => {
    if (this.busy) return true;
    if (!this.$panel || !this.isFacturaSelected()) return false;
    return !(this.dataset.saved === 'true' && !this.isFormDirty());
  };

  updateCheckout = () => {
    this.setCheckoutDisabled(this.shouldBlockCheckout());
  };

  checkFormState = () => {
    const saved = this.dataset.saved === 'true';
    const valid = this.$form.checkValidity();
    const enabledInputs = Array.from(this.$form.querySelectorAll('input:not(:disabled)'));
    const dirty = enabledInputs.some((input) => input.value !== input.defaultValue);

    this.$button.disabled = !valid || (saved && !dirty);
    this.$clearButton.hidden = !saved;
    this.updateCheckout();
  };

  buildLabel = (data) => {
    const tipo = data['attributes[TipoCliente]'];
    const ruc = data['attributes[Ruc]'];

    if (tipo === 'empresa' && data['attributes[RazonSocial]']) {
      return `<div><span class="pvt-invoicing-label">Factura:</span> ${data['attributes[RazonSocial]']} - ${ruc}</div>`;
    }
    if (data['attributes[Nombre]']) {
      return `<div><span class="pvt-invoicing-label">Factura:</span> ${data['attributes[Nombre]']} ${data['attributes[Apellido]'] || ''} - ${ruc}</div>`;
    }
    return '';
  };

  handleRucChange = () => {
    this.$rutInput.value = this.$rutInput.value.replace(/\D/g, '').slice(0, 11);

    if (this.$rutInput.value === '' || validateRuc(this.$rutInput.value)) {
      this.$rutInput.setCustomValidity('');
      this.$rutErrorMessage.classList.remove('show');
    } else {
      this.$rutInput.setCustomValidity('Ingresa un RUC válido');
      this.$rutErrorMessage.classList.add('show');
    }

    this.checkFormState();
  };

  handleSubmit = (e) => {
    e.preventDefault();

    if (this.$rutInput.value && !validateRuc(this.$rutInput.value)) {
      this.$rutErrorMessage.classList.add('show');
      return;
    }

    const mode = this.getMode();
    const attributes = {
      TipoCliente: mode,
      Nombre: mode === 'persona' ? this.querySelector('input[name="attributes[Nombre]"]').value : '',
      Apellido: mode === 'persona' ? this.querySelector('input[name="attributes[Apellido]"]').value : '',
      RazonSocial: mode === 'empresa' ? this.querySelector('input[name="attributes[RazonSocial]"]').value : '',
      Giro: mode === 'empresa' ? this.querySelector('input[name="attributes[Giro]"]').value : '',
      RucPersona: mode === 'persona' ? this.$rutInput.value : '',
      RucEmpresa: mode === 'empresa' ? this.$rutInput.value : '',
      DireccionEmpresa: this.querySelector('input[name="attributes[DireccionEmpresa]"]').value,
      Ciudad: this.querySelector('input[name="attributes[Ciudad]"]').value,
      Comuna: this.querySelector('input[name="attributes[Comuna]"]').value,
      Email: this.querySelector('input[name="attributes[Email]"]').value,
    };

    this.$button.disabled = true;
    this.$button.textContent = 'Guardando...';
    this.$clearButton.disabled = true;
    this.busy = true;
    this.updateCheckout();

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ attributes }),
    })
      .then(() => {
        const data = Object.fromEntries(
          Object.entries(attributes).map(([key, value]) => [`attributes[${key}]`, value])
        );
        data['attributes[Ruc]'] = this.$rutInput.value;
        if (this.$label) this.$label.innerHTML = this.buildLabel(data) || '¿Necesitas factura?';
        this.dataset.saved = 'true';
        this.editing = false;
        if (this.$summaryText) this.$summaryText.textContent = this.buildSummaryText(data);
        this.renderSelectorState();
        this.$form.querySelectorAll('input:not(:disabled)').forEach((input) => {
          input.defaultValue = input.value;
        });
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        this.$button.disabled = false;
        this.$button.textContent = 'Guardar';
        this.$clearButton.disabled = false;
        this.busy = false;
        this.updateCheckout();
        this.checkFormState();
      });
  };

  handleClear = () => {
    const attributes = {
      TipoCliente: null,
      Nombre: null,
      Apellido: null,
      RazonSocial: null,
      RucPersona: null,
      RucEmpresa: null,
      RUTEmpresa: null,
      Giro: null,
      DireccionEmpresa: null,
      Ciudad: null,
      Comuna: null,
      Email: null,
      Region: null,
    };
    this.$rutErrorMessage.classList.remove('show');
    this.$clearButton.disabled = true;
    this.$clearButton.textContent = 'Limpiando...';
    this.$button.disabled = true;
    this.busy = true;
    this.updateCheckout();

    fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({ attributes }),
    })
      .then(() => {
        this.$form.querySelectorAll('input:not([type="radio"])').forEach((el) => {
          el.value = '';
          el.defaultValue = '';
        });
        this.$tipoRadios.forEach((radio) => (radio.checked = radio.value === 'persona'));
        this.handleModeChange();
        if (this.$label) this.$label.textContent = '¿Necesitas factura?';
        this.dataset.saved = 'false';
        this.editing = false;
        this.renderSelectorState();
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        this.$button.disabled = false;
        this.$button.textContent = 'Guardar';
        this.$clearButton.disabled = false;
        this.$clearButton.textContent = 'Limpiar';
        this.busy = false;
        this.updateCheckout();
        this.checkFormState();
      });
  };
}

if (!customElements.get('pivot-invoice-form')) {
  customElements.define('pivot-invoice-form', PivotInvoiceForm);
}

function validateRuc(ruc) {
  if (!/^\d{11}$/.test(ruc)) return false;

  const validPrefixes = ['10', '15', '17', '20'];
  if (!validPrefixes.includes(ruc.slice(0, 2))) return false;

  const weights = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  const suma = weights.reduce((acc, weight, index) => acc + weight * parseInt(ruc[index], 10), 0);
  const resto = suma % 11;
  let dv = 11 - resto;
  if (dv === 10) dv = 0;
  if (dv === 11) dv = 1;

  return dv === parseInt(ruc[10], 10);
}
